import { z } from 'zod'

import { money } from '@/lib/money'

/**
 * Field shapes shared by more than one domain.
 *
 * `moneySchema` is the boundary where an untyped decimal string becomes a
 * branded `Money`. Declaring it once matters: a domain that reached for
 * `z.string()` instead would type-check happily and then hand a bare string to
 * `formatNPR`, which is exactly the hole the brand exists to close.
 */

/** A DRF `DecimalField` on the wire: "2622.48", "-19.34", "0". Percentages
 *  travel the same way and are also Money — they are decimals, not counts. */
export const moneySchema = z
  .string()
  .regex(/^-?\d+(\.\d+)?$/, 'Expected a decimal string, e.g. "512.00"')
  .transform(money)

/**
 * A string search param, as a URL typed or pasted by hand actually delivers it.
 *
 * TanStack Router's default search codec JSON-parses every value, so a bare
 * `?broker=58` or `?q=2080` arrives as the NUMBER, and a plain `z.string()`
 * rejects it — the route throws instead of rendering. (Values the app writes
 * itself through `navigate`/`Link` are quoted by the codec and round-trip as
 * strings; the trap is links typed, pasted or built as plain hrefs.) This
 * widens a number back to the text it was typed as. Broker numbers print a
 * leading zero ("07"), which is not valid JSON and so already stays a string.
 */
export const searchTextParamSchema = z.union([z.string(), z.number()]).transform(String)

/** `searchTextParamSchema`, non-empty: a symbol, a broker number, a filter id. */
export const codeParamSchema = searchTextParamSchema.pipe(z.string().min(1))

/**
 * Bikram Sambat display date ("2 Bhadra 2083").
 *
 * API AD dates are converted in src/lib/bsDate.ts. This is a display string,
 * never a Date; the shape check does not police the underlying calendar.
 */
export const bsDateSchema = z
  .string()
  .regex(/^\d{1,2} [A-Z][a-z]+ \d{4}$/, 'Expected a Bikram Sambat date, e.g. "2 Bhadra 2083"')

/**
 * `bsDateSchema`, or the '—' `formatBsDate` prints for a null wire date. An
 * as-of line built from a nullable timestamp (`MarketSummary.as_of` is
 * `allow_null`) must not fail the page it heads.
 */
export const bsDateOrDashSchema = z.union([bsDateSchema, z.literal('—')])

/**
 * A list parsed row by row: a row that fails `schema` is dropped and logged,
 * and every well-formed row beside it survives.
 *
 * For server lists whose rows are independent facts — one malformed plan,
 * payment or price row must not blank the whole catalogue, history or table.
 * The `console.warn` keeps a real data problem visible instead of silently
 * vanishing a row. A row's identity fields stay strict in its own schema, so
 * a row with no symbol or no price is the one that goes.
 */
export function lenientList<T>(schema: z.ZodType<T>, label: string) {
  return z.array(z.unknown()).transform((rows) =>
    rows.flatMap((row) => {
      const parsed = schema.safeParse(row)
      if (parsed.success) return [parsed.data]
      console.warn(`dropped an unparseable ${label} row`, parsed.error, row)
      return []
    }),
  )
}

/**
 * A text field mm-be may send as `""` as well as null. Both mean "not written",
 * so a screen never prints an empty string where it meant a dash, and a
 * schema's `min(1)` never fails a whole list over one blank.
 */
export const blankToNull = (value: string | null): string | null =>
  value === null || value.trim() === '' ? null : value

/**
 * One of a closed vocabulary, or null.
 *
 * mm-be serialises every code-like field as a bare CharField, so a value this
 * build does not know is an upstream fact, not a shape error — narrowed to
 * null here rather than parsed against the closed enum, so it costs one chip
 * instead of failing the read that carries it. Here rather than in a feature's
 * service because the reference table and the company panel both narrow their
 * words this way.
 */
export const knownWord = <T extends string>(
  vocabulary: readonly T[],
  value: string | null,
): T | null =>
  value !== null && (vocabulary as readonly string[]).includes(value) ? (value as T) : null

/**
 * The categorical colour a row, dot or chip carries.
 *
 * A tone, never a colour: the semantic token utility is chosen by the
 * component (`text-market-up`, `bg-tone-violet-soft`), so a palette swap never
 * reaches the data layer. `up`/`down`/`flat` are directional; the rest are the
 * categorical tones from the token set.
 */
export const toneSchema = z.enum([
  'up',
  'down',
  'flat',
  'accent',
  'saffron',
  'violet',
  'teal',
  'muted',
])

export type Tone = z.infer<typeof toneSchema>

/**
 * A DRF `PERCENT` decimal on the wire: "12.34" means 12.34 %, not 0.1234.
 *
 * Deliberately the SAME schema and the same `Money` brand as above, aliased
 * rather than redeclared. A percent is a decimal that arrives as a string and
 * must not be added with `+` either, so a parallel `Percent` brand would buy
 * nothing and cost every call site a conversion — `formatPercent` and
 * `<Change percent>` already take `Money`. The alias exists so a field named
 * `unrealized_pl_percent` reads as a percent where it is declared, instead of
 * looking like a rupee figure.
 */
export const percentSchema = moneySchema
